import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { tasksAPI, usersAPI, managerAPI, adminAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext';

const TaskContext = createContext(null);

export const useTask = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTask must be used within TaskProvider');
  }
  return context;
};

export const TaskProvider = ({ children }) => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const PENDING_KEY = 'pendingSubtaskPatches';
  const flushTimerRef = React.useRef(null);

  const loadPending = () => {
    try {
      return JSON.parse(localStorage.getItem(PENDING_KEY) || '{}');
    } catch {
      return {};
    }
  };

  const savePending = (obj) => {
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify(obj || {}));
    } catch { }
  };

  const computeBackoffMs = (attempts) => {
    const base = 3000; // 3s
    const max = 5 * 60 * 1000; // 5 minutes
    const ms = base * Math.pow(2, Math.max(0, attempts - 1));
    return Math.min(ms, max);
  };

  // Normalize task object to include `assignedTo` (usernames).
  const normalizeTask = (t) => {
    if (!t) return t;
    const assignees = t.assignees || [];
    const assignedTo = assignees
      .map((u) => {
        if (!u) return null;
        if (typeof u === 'string') {
          const found = users.find((x) => x._id === u || x.id === u);
          return found?.username || u;
        }
        return u.username || u.name || u;
      })
      .filter(Boolean);
    return { ...t, assignedTo };
  };

  const flushPending = async () => {
    const now = Date.now();
    const pending = loadPending();
    const ids = Object.keys(pending);
    if (ids.length === 0) return;
    let earliestNext = null;
    const WARN_ATTEMPTS = 3;
    const MAX_ATTEMPTS = 8;
    for (const taskId of ids) {
      const payload = pending[taskId];
      if (!payload || !Array.isArray(payload.subtasks)) continue;
      const attempts = Number(payload.attempts || 0);
      const nextTryAt = Number(payload.nextTryAt || 0);
      if (nextTryAt && nextTryAt > now) {
        earliestNext = earliestNext === null ? nextTryAt : Math.min(earliestNext, nextTryAt);
        continue; // skip until its scheduled time
      }
      try {
        const response = await tasksAPI.update(taskId, { subtasks: payload.subtasks });
        console.log(`Flushed pending subtasks for task ${taskId} after ${attempts} attempts`, response.data);
        const updatedTask = response.data.task || response.data;
        setTasks((prev) => prev.map((t) => (t._id === taskId ? normalizeTask(updatedTask) : t)));
        const p2 = loadPending();
        delete p2[taskId];
        savePending(p2);
      } catch (err) {
        // increment attempts and schedule next try
        const p2 = loadPending();
        const newAttempts = attempts + 1;
        const delay = computeBackoffMs(newAttempts);
        p2[taskId] = { ...payload, attempts: newAttempts, nextTryAt: Date.now() + delay };
        savePending(p2);
        // Global feedback on repeated failures
        if (newAttempts === WARN_ATTEMPTS) {
          toast.warning('Unsynced changes are queued. Use "Sync Now" in the header or check your connection.');
        }
        if (newAttempts === MAX_ATTEMPTS) {
          toast.error('Unsynced changes persist after multiple retries. Try "Sync Now" and verify network/API.');
        }
        earliestNext = earliestNext === null ? p2[taskId].nextTryAt : Math.min(earliestNext, p2[taskId].nextTryAt);
      }
    }
    // schedule next flush based on the earliest nextTryAt
    if (earliestNext !== null) {
      const wait = Math.max(0, earliestNext - Date.now());
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(flushPending, wait);
    }
  };

  const scheduleFlush = () => {
    // find earliest nextTryAt across pending
    const pending = loadPending();
    const times = Object.values(pending)
      .map(p => Number(p.nextTryAt || Date.now() + 1000))
      .filter(Boolean);
    const earliest = times.length ? Math.min(...times) : Date.now() + 1000;
    const delay = Math.max(0, earliest - Date.now());
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(flushPending, delay);
  };

  // Helper: display toast errors from server response (supports array of errors)
  const handleErrorToast = (err, fallbackMessage) => {
    try {
      const errors = err?.response?.data?.errors;
      if (Array.isArray(errors) && errors.length) {
        const msgs = errors.map(e => e.msg || e.message || String(e));
        msgs.forEach(m => toast.error(m));
        return msgs.join('; ');
      }
    } catch (e) {
      // ignore
    }
    const message = err?.response?.data?.message || fallbackMessage;
    toast.error(message);
    return message;
  };

  // Load tasks
  const loadTasks = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    try {
      const response = await tasksAPI.getAll();
      const tasksArr = response.data.tasks || response.data || [];
      setTasks(
        tasksArr.map((t) => ({
          ...t,
          assignedTo: (t.assignees || []).map((u) => u.username || u).filter(Boolean),
        }))
      );
    } catch (err) {
      const message = handleErrorToast(err, 'Failed to load tasks');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load users
  const loadUsers = useCallback(async () => {
    if (!user) return;

    try {
      const response = await usersAPI.getAll();
      const usersList = response.data.users || response.data || [];
      setUsers(usersList);
      // re-normalize existing tasks now that we have the users list
      setTasks((prev) => prev.map((t) => normalizeTask(t || {})));
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }, [user]);

  // Load projects (for task-to-project assignment)
  const loadProjects = useCallback(async () => {
    if (!user) return;
    try {
      let projectsList = [];
      if (user.role === 'admin' || user.role === 'hr') {
        const res = await adminAPI.getProjects();
        projectsList = res.data.projects || res.data || [];
      } else {
        const res = await usersAPI.getMyProjects();
        projectsList = res.data.projects || res.data || [];
      }
      setProjects(projectsList);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  }, [user]);

  // Load data on mount
  useEffect(() => {
    if (user) {
      loadTasks();
      loadUsers();
      loadProjects();
      // attempt to flush any pending subtask patches after initial data load
      setTimeout(() => flushPending(), 500);
    }
  }, [user, loadTasks, loadUsers, loadProjects]);

  // Create task
  const createTask = async (taskData) => {
    try {
      const response = await tasksAPI.create(taskData);
      const newTask = response.data.task || response.data;
      setTasks((prev) => [...prev, normalizeTask(newTask)]);
      toast.success('Task created successfully!');
      return { success: true, task: newTask };
    } catch (err) {
      const message = handleErrorToast(err, 'Failed to create task');
      console.error('Create task error:', err);
      return { success: false, error: message };
    }
  };

  // Update task
  const updateTask = async (id, taskData) => {
    try {
      const response = await tasksAPI.update(id, taskData);
      console.log('Update task response:', response.data);
      const updatedTask = response.data.task || response.data;
      setTasks((prev) => prev.map((task) => (task._id === id ? normalizeTask(updatedTask) : task)));
      toast.success('Task updated successfully!');
      return { success: true, task: updatedTask };
    } catch (err) {
      const message = handleErrorToast(err, 'Failed to update task');
      return { success: false, error: message };
    }
  };

  // Delete task
  const deleteTask = async (id) => {
    try {
      await tasksAPI.delete(id);
      setTasks((prev) => prev.filter((task) => task._id !== id));
      toast.success('Task deleted successfully!');
      return { success: true };
    } catch (err) {
      const message = handleErrorToast(err, 'Failed to delete task');
      return { success: false, error: message };
    }
  };

  // Update task status (for drag and drop)
  const updateTaskStatus = async (id, status) => {
    try {
      const response = await tasksAPI.updateStatus(id, status);
      const updatedTask = response.data.task || response.data;
      setTasks((prev) => prev.map((task) => (task._id === id ? normalizeTask(updatedTask) : task)));
      return { success: true, task: updatedTask };
    } catch (err) {
      const message = handleErrorToast(err, 'Failed to update status');
      return { success: false, error: message };
    }
  };

  // Assign user to task
  const assignUserToTask = async (taskId, userId) => {
    try {
      const response = await tasksAPI.assignUser(taskId, userId);
      const updatedTask = response.data.task || response.data;
      setTasks((prev) => prev.map((task) => (task._id === taskId ? normalizeTask(updatedTask) : task)));
      toast.success('User assigned successfully!');
      return { success: true, task: updatedTask };
    } catch (err) {
      const message = handleErrorToast(err, 'Failed to assign user');
      return { success: false, error: message };
    }
  };

  // Optimistic subtask completion toggle
  const setSubtaskCompleted = (taskId, index, completed) => {
    setTasks((prev) =>
      prev.map((t) => {
        if (t._id !== taskId || !Array.isArray(t.subtasks)) return t;
        const subtasks = t.subtasks.map((s, i) =>
          i === index
            ? (typeof s === 'string' ? { title: s, completed } : { ...s, completed })
            : s
        );
        // queue to localStorage
        const pending = loadPending();
        const attempts = (pending[taskId]?.attempts) || 0;
        pending[taskId] = { subtasks, ts: Date.now(), attempts, nextTryAt: Date.now() + 1000 };
        savePending(pending);
        // schedule background flush
        scheduleFlush();
        return { ...t, subtasks };
      })
    );
  };

  const hasPendingFor = (taskId) => {
    const pending = loadPending();
    return !!pending[taskId];
  };

  // Assign task to project
  const assignTaskToProject = async (taskId, projectId) => {
    try {
      const response = await tasksAPI.assignProject(taskId, projectId);
      const updatedTask = response.data.task || response.data;
      setTasks((prev) => prev.map((task) => (task._id === taskId ? normalizeTask(updatedTask) : task)));
      toast.success(projectId ? 'Task assigned to project' : 'Task removed from project');
      loadProjects(); // refresh project task counts
      return { success: true, task: updatedTask };
    } catch (err) {
      const message = handleErrorToast(err, 'Failed to assign project');
      return { success: false, error: message };
    }
  };

  const value = {
    tasks,
    users,
    projects,
    loading,
    error,
    loadTasks,
    loadProjects,
    createTask,
    updateTask,
    deleteTask,
    updateTaskStatus,
    assignUserToTask,
    assignTaskToProject,
    setSubtaskCompleted,
    hasPendingFor,
    getPendingCount: () => {
      const pending = loadPending();
      return Object.keys(pending).length;
    },
    syncPendingNow: async () => {
      await flushPending();
    }
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};
